import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Form, Input, InputNumber, Popconfirm, Select, Space, Switch, Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useSaveSettings, useSettingsQuery } from '../hooks';

const { Text } = Typography;

const NUMBER_FIELDS = [
  { name: 'minCoverWeeks', label: 'Min cover (weeks)', help: 'Brew when projected stock covers fewer weeks than this' },
  { name: 'maxCoverWeeks', label: 'Max cover (weeks)', help: 'Size brews so stock stays under this (8.7 ≈ 2 months)' },
  { name: 'leadTimeDays', label: 'Lead time (days)', help: 'Brew day to packaged and ready to sell' },
  { name: 'planningHorizonWeeks', label: 'Planning horizon (weeks)' },
  { name: 'packagingLossPct', label: 'Brew → packaged loss (%)' },
  { name: 'tankOverfillTolerancePct', label: 'Tank overshoot allowed (%)', help: 'How far a tank may exceed the ideal brew size' },
  { name: 'tankBeerOverdueDays', label: 'Overdue tank beer ready in (days)', help: 'For gyles past their expected ready date' },
  {
    name: 'rescheduleWindowWeeks',
    label: 'Bring-forward window (weeks)',
    help: 'If a planned brew lands within this many weeks of a dip, suggest bringing it forward instead of a new brew',
  },
  {
    name: 'skuTriggerMinShare',
    label: 'Min pack share to trigger',
    help: 'Pack triggers a brew only at this share of demand (0.1 = 10%). Ignores a slow tail pack.',
    step: 0.05,
    max: 1,
  },
  {
    name: 'minRemainingShelfDays',
    label: 'Min shelf life at sale (days)',
    help: 'Stock is at risk if it cannot sell this many days before best-before',
    step: 1,
  },
  {
    name: 'expiryWarnMinLitres',
    label: 'Expiry warning from (L)',
    help: 'Only warn when at-risk stock is at least this many litres',
    step: 1,
  },
];

const GlobalRules = ({ canEdit }) => {
  const [form] = Form.useForm();
  const settingsQuery = useSettingsQuery();
  const saveSettings = useSaveSettings();
  const { data } = settingsQuery;

  useEffect(() => {
    if (data) form.setFieldsValue(data.settings);
  }, [data, form]);

  const breweries = Form.useWatch('tanks', form) || [];

  const save = async () => {
    const values = await form.validateFields();
    await saveSettings.mutateAsync(values);
  };

  const resetAll = () => {
    const reset = {};
    Object.keys(data.defaults).forEach((key) => { reset[key] = null; });
    saveSettings.mutate(reset);
  };

  return (
    <Card
      className="forecasting-section"
      title="Global rules & targets"
      loading={settingsQuery.isLoading}
      extra={canEdit && (
        <Space>
          <Popconfirm title="Reset every rule to its default?" onConfirm={resetAll}>
            <Button>Reset to defaults</Button>
          </Popconfirm>
          <Button type="primary" onClick={save} loading={saveSettings.isLoading}>Save rules</Button>
        </Space>
      )}
    >
      <Form form={form} layout="vertical" disabled={!canEdit}>
        <Space wrap align="start" size="large">
          {NUMBER_FIELDS.map((f) => (
            <Form.Item
              key={f.name}
              name={f.name}
              label={f.label}
              tooltip={f.help}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={f.max} step={f.step || 0.5} style={{ width: 170 }} />
            </Form.Item>
          ))}
          <Form.Item name="defaultBrewery" label="Default brewery">
            <Select
              style={{ width: 170 }}
              options={breweries
                .filter((t) => t?.brewery)
                .map((t) => ({ value: t.brewery, label: t.brewery }))}
            />
          </Form.Item>
          <Form.Item name="countTankBeer" label="Count beer in tanks" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="excludeExpiredStock" label="Ignore expired stock" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>

        <Text strong>Tanks</Text>
        <Form.List name="tanks">
          {(fields, { add, remove }) => (
            <div style={{ marginTop: 8 }}>
              {fields.map((field) => (
                <Space key={field.key} align="baseline" wrap>
                  <Form.Item {...field} name={[field.name, 'brewery']} rules={[{ required: true, message: 'Brewery' }]}>
                    <Input style={{ width: 170 }} placeholder="Brewery" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'sizes']}
                    rules={[{ required: true, message: 'Tank sizes' }]}
                    normalize={(v) => (v || [])
                      .map(Number)
                      .filter((n) => n > 0)
                      .sort((a, b) => a - b)}
                  >
                    <Select mode="tags" style={{ width: 420 }} placeholder="Tank sizes in litres, e.g. 10000" />
                  </Form.Item>
                  {canEdit && <MinusCircleOutlined onClick={() => remove(field.name)} />}
                </Space>
              ))}
              {canEdit && (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ brewery: '', sizes: [] })}>
                  Add brewery
                </Button>
              )}
            </div>
          )}
        </Form.List>
      </Form>
    </Card>
  );
};

GlobalRules.propTypes = { canEdit: PropTypes.bool.isRequired };

export default GlobalRules;
